import { Close, Save } from '@mui/icons-material';
import {
	Button,
	Dialog,
	DialogContent,
	DialogTitle,
	FormGroup,
	IconButton,
	LinearProgress,
	List,
	ListItem,
	ListItemText,
	ListSubheader,
	TextField,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

import useSuccessSnackbar from 'hooks/useSuccessSnakbar';
import { enqueueSnackbarError } from 'lib/helpers';
import { createMerchCategory, updateMerchCategory } from 'lib/models/merchCategories';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmationDialog from 'components/ConfirmationDialog';
import { deleteMerch } from 'lib/models/merch';

type Props = {
	defaultValue: AppMerchCategory;
	onClose: () => void;
	refetch: () => void;
};

export default function MerchCategoryModal({ defaultValue, onClose, refetch }: Props) {
	const { t } = useTranslation();
	const successSnackbar = useSuccessSnackbar();

	const [loading, setLoading] = useState(false);
	const [merchCategory, setMerchCategory] = useState(defaultValue);
	const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<AppMerchandising | null>(null);

	const onUpdate = (key: keyof AppMerchCategory) => {
		return (e: React.ChangeEvent<HTMLInputElement>) => {
			const { value } = e.target;
			setMerchCategory({ ...merchCategory, [key]: value });
		};
	};

	const onDeleteHandler = async (mustDelete: boolean) => {
		if (mustDelete && showDeleteConfirmation?._id) {
			const deleted = await deleteMerch(showDeleteConfirmation);

			if (!deleted) enqueueSnackbarError(t('merch:deletionError'));
			else {
				const newItems = defaultValue.items.filter((items) => items._id !== showDeleteConfirmation._id);
				setMerchCategory({ ...merchCategory, items: newItems });
				successSnackbar();
				refetch();
			}
		}

		setShowDeleteConfirmation(null);
	};

	const onDelete = (product: AppMerchandising) => {
		setShowDeleteConfirmation(product);
	};

	const save = async () => {
		setLoading(true);
		try {
			if (merchCategory?._id) await updateMerchCategory(merchCategory);
			else await createMerchCategory(merchCategory);
			successSnackbar();
		} catch (error) {
			enqueueSnackbarError(error);
		}
		setLoading(false);
		onClose();
	};

	return (
		<Dialog open>
			{showDeleteConfirmation && (
				<ConfirmationDialog
					title={t('common:deleteTitle')}
					description={t('common:deleteText')}
					onClose={onDeleteHandler}
				/>
			)}
			{loading && <LinearProgress />}
			<DialogTitle>{t('merch:merchandising')}</DialogTitle>
			<DialogContent sx={{ minWidth: '30vw' }}>
				{loading && <LinearProgress sx={{ mb: 3 }} />}
				{merchCategory && (
					<FormGroup sx={{ paddingTop: 1 }}>
						<TextField
							id="title"
							label={t('common:title')}
							fullWidth={true}
							value={merchCategory.title}
							variant="outlined"
							sx={{ mb: 3 }}
							onChange={onUpdate('title')}
							disabled={loading}
						/>
						{defaultValue.items.length > 0 && (
							<List
								sx={{
									width: '100%',
									maxWidth: 360,
									bgcolor: 'background.paper',
									position: 'relative',
									overflow: 'auto',
									maxHeight: 300,
									paddingTop: 0,
								}}
							>
								<ListSubheader>{t('common:merch')}</ListSubheader>
								{merchCategory.items.map((item) => (
									<ListItem
										key={item._id}
										secondaryAction={
											<IconButton onClick={() => onDelete(item)} edge="end" aria-label="delete">
												<DeleteIcon />
											</IconButton>
										}
									>
										<ListItemText primary={item.title} />
									</ListItem>
								))}
							</List>
						)}

						<Button
							variant="contained"
							color="primary"
							size="large"
							sx={{ m: 1 }}
							startIcon={<Save />}
							onClick={save}
							disabled={!merchCategory.title}
						>
							{t('common:save')}
						</Button>

						<Button
							variant="contained"
							color="secondary"
							size="large"
							sx={{ m: 1 }}
							startIcon={<Close />}
							onClick={onClose}
						>
							{t('common:cancel')}
						</Button>
					</FormGroup>
				)}
			</DialogContent>
		</Dialog>
	);
}
